import { AxiosResponse, isAxiosError } from 'axios';
import telemetryService from '|/app/services/telemetry/telemetry-service';
import axiosInstance from '|/helpers/axios';
import {
  CollectPaymentResponse,
  CreateStripeSetupIntentResponse,
  GetPaymentSummaryResponse,
  InitiateRequestForPaymentResponse,
  AddCardFromStripeRequest,
  SubmitConsentsRequest
} from './purchase-payments-api.types';
import getElapsedTimeInSeconds from '../utils/getElapsedTimeInSeconds';

const baseUrl = `${process.env.PURCHASE_PAYMENTS_API_URL}/api`;

export const PAYMENT_SUMMARY_ENDPOINT = `${baseUrl}/v1/pay-at-order/summary`;

export const fetchPaymentSummary = (abortSignal?: AbortSignal) => {
  const startTime = performance.now();

  telemetryService.trackEvent({
    name: 'purchase-payments-api:fetchPaymentSummary:start'
  });

  return axiosInstance
    .get<never, AxiosResponse<GetPaymentSummaryResponse>>(PAYMENT_SUMMARY_ENDPOINT, {
      signal: abortSignal
    })
    .then(res => {
      const elapsedTimeInSeconds = getElapsedTimeInSeconds(startTime);

      telemetryService.trackEvent({
        name: 'purchase-payments-api:fetchPaymentSummary:success',
        context: {
          elapsedTimeInSeconds: elapsedTimeInSeconds
        }
      });

      return res.data;
    })
    .catch((err: unknown) => {
      const elapsedTimeInSeconds = getElapsedTimeInSeconds(startTime);

      telemetryService.trackError({
        error: err,
        name: 'purchase-payments-api:fetchPaymentSummary:error',
        context: {
          elapsedTimeInSeconds: elapsedTimeInSeconds
        }
      });

      return Promise.reject(
        err instanceof Error ? err : new Error('Failed to fetch payment summary.')
      );
    });
};

export const submitConsents = (
  consents: SubmitConsentsRequest['consents'],
  abortSignal?: AbortSignal
) => {
  const startTime = performance.now();

  telemetryService.trackEvent({
    name: 'purchase-payments-api:submitConsents:start',
    context: {
      hasConsents: !!consents
    }
  });

  return axiosInstance
    .post<{ consents: SubmitConsentsRequest['consents'] }, AxiosResponse<void>>(
      `${baseUrl}/v1/pay-at-order/submit-consents`,
      { consents },
      { signal: abortSignal }
    )
    .then(res => {
      const elapsedTimeInSeconds = getElapsedTimeInSeconds(startTime);

      telemetryService.trackEvent({
        name: 'purchase-payments-api:submitConsents:success',
        context: {
          elapsedTimeInSeconds: elapsedTimeInSeconds,
          hasConsents: !!consents
        }
      });

      return res.data;
    })
    .catch((err: unknown) => {
      const elapsedTimeInSeconds = getElapsedTimeInSeconds(startTime);

      telemetryService.trackError({
        error: err,
        name: 'purchase-payments-api:submitConsents:error',
        context: {
          elapsedTimeInSeconds: elapsedTimeInSeconds,
          hasConsents: !!consents
        }
      });

      return Promise.reject(err instanceof Error ? err : new Error('Failed to submit consents.'));
    });
};

export const initiateRequestForPayment = (): Promise<InitiateRequestForPaymentResponse> =>
  axiosInstance
    .post<
      never,
      AxiosResponse<InitiateRequestForPaymentResponse>
    >(`${baseUrl}/v1/pay-at-order/initiate-request-for-payment`)
    .then(res => res.data);

export const collectPayment = (
  consents: SubmitConsentsRequest['consents'],
  abortSignal?: AbortSignal
) => {
  const startTime = performance.now();

  telemetryService.trackEvent({
    name: 'purchase-payments-api:collectPayment:start'
  });

  return axiosInstance
    .post<{ consents: SubmitConsentsRequest['consents'] }, AxiosResponse<CollectPaymentResponse>>(
      `${baseUrl}/v1/pay-at-order/collect-payment`,
      { consents },
      { signal: abortSignal }
    )
    .then(res => {
      const elapsedTimeInSeconds = getElapsedTimeInSeconds(startTime);

      telemetryService.trackEvent({
        name: 'purchase-payments-api:collectPayment:success',
        context: {
          elapsedTimeInSeconds: elapsedTimeInSeconds,
          paymentCollectionSucceeded: res.data.paymentCollectionSucceeded
        }
      });

      return res.data;
    })
    .catch((err: unknown) => {
      const elapsedTimeInSeconds = getElapsedTimeInSeconds(startTime);

      telemetryService.trackError({
        error: err,
        name: 'purchase-payments-api:collectPayment:error',
        context: {
          elapsedTimeInSeconds: elapsedTimeInSeconds
        }
      });

      return Promise.reject(err instanceof Error ? err : new Error('Failed to collect payment.'));
    });
};

export const createStripeSetupIntent = (abortSignal?: AbortSignal) => {
  const startTime = performance.now();

  telemetryService.trackEvent({
    name: 'purchase-payments-api:createStripeSetupIntent:start'
  });

  return axiosInstance
    .post<never, AxiosResponse<CreateStripeSetupIntentResponse>>(
      `${baseUrl}/v1/cards/create-stripe-setup-intent`,
      undefined,
      { signal: abortSignal }
    )
    .then(res => {
      const elapsedTimeInSeconds = getElapsedTimeInSeconds(startTime);

      telemetryService.trackEvent({
        name: 'purchase-payments-api:createStripeSetupIntent:success',
        context: {
          elapsedTimeInSeconds: elapsedTimeInSeconds
        }
      });

      return res.data;
    })
    .catch((err: unknown) => {
      const elapsedTimeInSeconds = getElapsedTimeInSeconds(startTime);

      telemetryService.trackError({
        error: err,
        name: 'purchase-payments-api:createStripeSetupIntent:error',
        context: {
          elapsedTimeInSeconds: elapsedTimeInSeconds
        }
      });

      return Promise.reject(
        err instanceof Error ? err : new Error('Failed to create Stripe setup intent.')
      );
    });
};

export const addAndSelectCardFromDigitalWallet = (
  body: AddCardFromStripeRequest,
  abortSignal?: AbortSignal
): Promise<{ hasError: false } | { hasError: true; customerErrorMessage: string | undefined }> => {
  const startTime = performance.now();

  telemetryService.trackEvent({
    name: 'purchase-payments-api:addAndSelectCardFromDigitalWallet:start'
  });

  return axiosInstance
    .post<AddCardFromStripeRequest, AxiosResponse<void>>(
      `${baseUrl}/v1/pay-at-order/add-and-select-card-from-digital-wallet`,
      body,
      { signal: abortSignal }
    )
    .then(() => {
      const elapsedTimeInSeconds = getElapsedTimeInSeconds(startTime);

      telemetryService.trackEvent({
        name: 'purchase-payments-api:addAndSelectCardFromDigitalWallet:success',
        context: {
          elapsedTimeInSeconds: elapsedTimeInSeconds
        }
      });

      return { hasError: false } as const;
    })
    .catch((err: unknown) => {
      const elapsedTimeInSeconds = getElapsedTimeInSeconds(startTime);

      telemetryService.trackError({
        error: err,
        name: 'purchase-payments-api:addAndSelectCardFromDigitalWallet:error',
        context: {
          elapsedTimeInSeconds: elapsedTimeInSeconds
        }
      });

      const status = isAxiosError(err) ? err.response?.status : undefined;
      const isClientError = status !== undefined && status >= 400 && status < 500;

      if (isClientError) {
        const data = isAxiosError(err)
          ? (err.response?.data as { customerErrorMessage?: unknown } | undefined)
          : undefined;
        const customerErrorMessage =
          typeof data?.customerErrorMessage === 'string' ? data.customerErrorMessage : undefined;

        return { hasError: true, customerErrorMessage } as const;
      }

      return Promise.reject(
        err instanceof Error ? err : new Error('Failed to add and select card from digital wallet.')
      );
    });
};
